import { PdfPlacement } from './pdf-placement';

export type PdfSignEditorError = 'generate' | 'upload' | 'password' | 'too-large';

export interface PdfSignEditorState {
  mode: 'idle' | 'drawing' | 'generating';
  placements: PdfPlacement[];
  /** The placement currently unlocked for moving/resizing. */
  activeId: string | null;
  /** Placement whose image is being replaced by the open drawing panel. */
  editingId: string | null;
  error: PdfSignEditorError | null;
}

export type PdfSignEditorAction =
  | { type: 'OPEN_DRAWING' }
  | { type: 'CANCEL_DRAWING' }
  | { type: 'EDIT_ACTIVE' }
  | { type: 'INSERT'; placement: PdfPlacement }
  | { type: 'UPDATE'; placement: PdfPlacement }
  | { type: 'ACTIVATE'; id: string }
  | { type: 'FINISH' }
  | { type: 'REMOVE'; id: string }
  | { type: 'GENERATE_START' }
  | { type: 'GENERATE_FAILED'; error: PdfSignEditorError }
  | { type: 'DISMISS_ERROR' };

export const initialPdfSignEditorState: PdfSignEditorState = {
  mode: 'idle',
  placements: [],
  activeId: null,
  editingId: null,
  error: null,
};

export const pdfSignEditorReducer = (state: PdfSignEditorState, action: PdfSignEditorAction): PdfSignEditorState => {
  // Placements are frozen while the signed copy is generated/uploaded.
  if (state.mode === 'generating' && action.type !== 'GENERATE_FAILED') return state;

  switch (action.type) {
    case 'OPEN_DRAWING':
      return { ...state, mode: 'drawing', editingId: null, error: null };
    case 'EDIT_ACTIVE':
      return state.activeId ? { ...state, mode: 'drawing', editingId: state.activeId, error: null } : state;
    case 'CANCEL_DRAWING':
      return { ...state, mode: 'idle', editingId: null };
    case 'INSERT': {
      const editing = state.editingId ? state.placements.find((p) => p.id === state.editingId) : undefined;
      if (editing) {
        const replacement = { ...action.placement, id: editing.id };
        return {
          ...state,
          mode: 'idle',
          editingId: null,
          activeId: editing.id,
          placements: state.placements.map((p) => (p.id === editing.id ? replacement : p)),
        };
      }
      return {
        ...state,
        mode: 'idle',
        editingId: null,
        activeId: action.placement.id,
        placements: [...state.placements, action.placement],
      };
    }
    case 'UPDATE':
      return {
        ...state,
        placements: state.placements.map((p) => (p.id === action.placement.id ? action.placement : p)),
      };
    case 'ACTIVATE':
      return state.placements.some((p) => p.id === action.id) ? { ...state, activeId: action.id } : state;
    case 'FINISH':
      return { ...state, activeId: null };
    case 'REMOVE':
      return {
        ...state,
        placements: state.placements.filter((p) => p.id !== action.id),
        activeId: state.activeId === action.id ? null : state.activeId,
      };
    case 'GENERATE_START':
      return state.placements.length ? { ...state, mode: 'generating', activeId: null, error: null } : state;
    case 'GENERATE_FAILED':
      return { ...state, mode: 'idle', error: action.error };
    case 'DISMISS_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

export type PdfSignEditorStep = 'empty' | 'drawing' | 'placing' | 'placed' | 'generating';

/** Drives the footer copy/actions. */
export const getPdfSignEditorStep = (state: PdfSignEditorState): PdfSignEditorStep => {
  if (state.mode === 'generating') return 'generating';
  if (state.mode === 'drawing') return 'drawing';
  if (state.activeId) return 'placing';
  return state.placements.length ? 'placed' : 'empty';
};
